import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { COLLECTION_KEYS, COLLECTIONS } from "@/pages/admin/collections.config";
import { ADMIN } from "@/constants/testIds";
import { LogOut, LayoutDashboard, Inbox, Search, FileText, Activity, Building2, Quote, Award, Newspaper, TrendingUp } from "lucide-react";

function Item({ to, icon: Icon, children, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
          isActive ? "bg-bronze text-cream" : "text-cream/70 hover:text-cream hover:bg-cream/10"
        }`
      }
    >
      <Icon size={16} /> {children}
    </NavLink>
  );
}

export default function AdminLayout() {
  const { user, signOutUser } = useAuth();
  const navigate = useNavigate();

  const logout = async () => {
    await signOutUser();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-beige/40" data-testid={ADMIN.layout}>
      <aside className="w-60 bg-ink text-cream flex flex-col" data-testid={ADMIN.nav}>
        <div className="px-5 py-6 border-b border-cream/10">
          <div className="font-serif text-lg">A Homely Abode</div>
          <div className="text-[0.62rem] tracking-[0.25em] uppercase text-bronze">Admin CMS</div>
        </div>
        <nav className="flex-1 py-4 space-y-1">
          <Item to="/admin/dashboard" icon={LayoutDashboard}>Dashboard</Item>
          <Item to="/admin/leads" icon={Inbox}>Leads</Item>
          <div className="px-4 pt-4 pb-1 text-[0.6rem] uppercase tracking-wider text-cream/40">Content</div>
          <Item to="/admin/listings" icon={Building2}>Listings</Item>
          <Item to="/admin/proven-results" icon={TrendingUp}>Proven Results</Item>
          <Item to="/admin/testimonials" icon={Quote}>Testimonials</Item>
          <Item to="/admin/awards" icon={Award}>Awards</Item>
          <Item to="/admin/blogs" icon={Newspaper}>Blogs</Item>
          {COLLECTION_KEYS.filter((k) => !["listings", "testimonials", "awards", "blogs", "provenResults"].includes(k)).map((k) => (
            <Item key={k} to={`/admin/content/${k}`} icon={FileText}>{COLLECTIONS[k].label}</Item>
          ))}
          <div className="px-4 pt-4 pb-1 text-[0.6rem] uppercase tracking-wider text-cream/40">Site</div>
          <Item to="/admin/seo" icon={Search}>SEO</Item>
          <Item to="/admin/tracking" icon={Activity}>Tracking</Item>
        </nav>
        <div className="border-t border-cream/10 p-4">
          <div className="text-xs text-cream/50 mb-2 truncate">{user?.email}</div>
          <button onClick={logout} data-testid={ADMIN.logout} className="flex items-center gap-2 text-sm text-cream/80 hover:text-cream">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-8 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
